import {
  AppBskyFeedLike,
  AppBskyFeedPost,
  AppBskyFeedRepost,
  AppBskyGraphFollow,
  AtUri,
} from '@atproto/api'
import { db } from '../cache/db'
import type { AiProvider } from './aiNames'

export type HandleFeatures = {
  handle: string
  totals: {
    posts: number
    replies: number
    likes: number
    reposts: number
    follows: number
  }
  /** Hour-of-day histogram (UTC), length 24 */
  hourBuckets: number[]
  /** Inter-action gap stats (ms) */
  medianGapMs: number
  meanGapMs: number
  subSecondBursts: number
  zeroMsTimestamps: number
  /** Sample of recent post text (truncated, max 50 lines) */
  samplePosts: string[]
  /** Top 15 hashtags by count */
  topHashtags: { tag: string; count: number }[]
  /** Top 10 DIDs they reply to most */
  topReplyTargets: { did: string; count: number }[]
  /** First / last action ISO timestamps */
  earliest: string | null
  latest: string | null
}

export type PairOverlap = {
  i: number
  j: number
  /** distinct seconds at which both handles had ≥1 action */
  secondOverlap: number
  /** distinct ms */
  msOverlap: number
}

export type CorrelationInput = {
  handles: HandleFeatures[]
  pairwise: PairOverlap[]
}

const HASHTAG_RE = /(?:^|\s)#([\p{L}\p{N}_]{2,40})/gu

function bucketTimestamps(ts: number[]) {
  const ms = new Set<number>()
  const sec = new Set<number>()
  for (const t of ts) {
    ms.add(t)
    sec.add(Math.floor(t / 1000))
  }
  return { ms, sec }
}

function intersect<T>(a: Set<T>, b: Set<T>): number {
  let n = 0
  const small = a.size < b.size ? a : b
  const big = a.size < b.size ? b : a
  for (const v of small) if (big.has(v)) n++
  return n
}

export async function gatherFeatures(handle: string): Promise<HandleFeatures> {
  const h = handle.toLowerCase()
  const [posts, likes, reposts, follows] = await Promise.all([
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.post']).toArray(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.like']).toArray(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.feed.repost']).toArray(),
    db.records.where('[handle+collection]').equals([h, 'app.bsky.graph.follow']).toArray(),
  ])

  const ts: number[] = []
  let postCount = 0
  let replyCount = 0
  const hashtagCount = new Map<string, number>()
  const replyTargets = new Map<string, number>()
  const samples: string[] = []

  for (const p of posts) {
    if (!AppBskyFeedPost.isRecord(p.value)) continue
    const t = new Date(p.value.createdAt).getTime()
    if (Number.isNaN(t)) continue
    ts.push(t)
    if (p.value.reply?.parent?.uri) {
      replyCount++
      try {
        const at = new AtUri(p.value.reply.parent.uri)
        replyTargets.set(at.hostname, (replyTargets.get(at.hostname) ?? 0) + 1)
      } catch {
        /* ignore */
      }
    } else {
      postCount++
    }
    if (typeof p.value.text === 'string' && p.value.text.length > 0 && samples.length < 50) {
      samples.push(p.value.text.slice(0, 280))
      let m: RegExpExecArray | null
      while ((m = HASHTAG_RE.exec(p.value.text)) !== null) {
        const tag = m[1].toLowerCase()
        hashtagCount.set(tag, (hashtagCount.get(tag) ?? 0) + 1)
      }
    }
  }
  for (const l of likes) {
    if (!AppBskyFeedLike.isRecord(l.value)) continue
    const t = new Date(l.value.createdAt).getTime()
    if (!Number.isNaN(t)) ts.push(t)
  }
  for (const r of reposts) {
    if (!AppBskyFeedRepost.isRecord(r.value)) continue
    const t = new Date(r.value.createdAt).getTime()
    if (!Number.isNaN(t)) ts.push(t)
  }
  for (const f of follows) {
    if (!AppBskyGraphFollow.isRecord(f.value)) continue
    const t = new Date(f.value.createdAt).getTime()
    if (!Number.isNaN(t)) ts.push(t)
  }

  ts.sort((a, b) => a - b)
  const hourBuckets = new Array(24).fill(0) as number[]
  let zeroMs = 0
  for (const t of ts) {
    hourBuckets[new Date(t).getUTCHours()]++
    if (t % 1000 === 0) zeroMs++
  }
  const gaps: number[] = []
  for (let i = 1; i < ts.length; i++) gaps.push(ts[i] - ts[i - 1])
  gaps.sort((a, b) => a - b)
  const median = gaps.length === 0 ? 0 : gaps[Math.floor(gaps.length / 2)]
  const mean = gaps.length === 0 ? 0 : gaps.reduce((s, g) => s + g, 0) / gaps.length
  const subSecond = gaps.filter((g) => g < 1000).length

  const topHashtags = Array.from(hashtagCount.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15)
  const topReplyTargets = Array.from(replyTargets.entries())
    .map(([did, count]) => ({ did, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)

  return {
    handle: h,
    totals: {
      posts: postCount,
      replies: replyCount,
      likes: likes.length,
      reposts: reposts.length,
      follows: follows.length,
    },
    hourBuckets,
    medianGapMs: Math.round(median),
    meanGapMs: Math.round(mean),
    subSecondBursts: subSecond,
    zeroMsTimestamps: zeroMs,
    samplePosts: samples,
    topHashtags,
    topReplyTargets,
    earliest: ts.length > 0 ? new Date(ts[0]).toISOString() : null,
    latest: ts.length > 0 ? new Date(ts[ts.length - 1]).toISOString() : null,
  }
}

async function gatherTimestampsOnly(handle: string): Promise<number[]> {
  const h = handle.toLowerCase()
  const all: number[] = []
  await db.records
    .where('handle')
    .equals(h)
    .each((r) => {
      const v = r.value as { createdAt?: string }
      const c = v?.createdAt
      if (typeof c === 'string') {
        const t = new Date(c).getTime()
        if (!Number.isNaN(t)) all.push(t)
      }
    })
  return all
}

export async function buildCorrelationInput(handles: string[]): Promise<CorrelationInput> {
  const features = await Promise.all(handles.map((h) => gatherFeatures(h)))
  const tsLists = await Promise.all(handles.map((h) => gatherTimestampsOnly(h)))
  const sets = tsLists.map(bucketTimestamps)
  const pairwise: PairOverlap[] = []
  for (let i = 0; i < handles.length; i++) {
    for (let j = i + 1; j < handles.length; j++) {
      pairwise.push({
        i,
        j,
        secondOverlap: intersect(sets[i].sec, sets[j].sec),
        msOverlap: intersect(sets[i].ms, sets[j].ms),
      })
    }
  }
  return { handles: features, pairwise }
}

/* ───────── AI prompt + provider call ───────── */

export type CorrelationVerdict = {
  pairs: Array<{
    a: string
    b: string
    /** 0..1 */
    likelihoodSamePerson: number
    /** "low" / "medium" / "high" / "very high" */
    confidence: string
    /** Brief one-paragraph reasoning */
    reasoning: string
    /** Concrete signals the model leaned on */
    signals: string[]
  }>
  /** Overall verdict for the whole set (when handles.length >= 3) */
  groupVerdict: {
    likelihoodAllSame: number
    reasoning: string
  }
}

const SYSTEM_PROMPT = `You are an analyst inspecting Bluesky account telemetry for sockpuppet / shared-operator overlap.
Return STRICT JSON, no prose, no markdown. Schema:
{
  "pairs": [
    {
      "a": "<handle>",
      "b": "<handle>",
      "likelihoodSamePerson": <number 0..1>,
      "confidence": "low" | "medium" | "high" | "very high",
      "reasoning": "<one short paragraph>",
      "signals": [ "<short bullet>", ... up to 6 ]
    }
  ],
  "groupVerdict": {
    "likelihoodAllSame": <number 0..1>,
    "reasoning": "<one short paragraph>"
  }
}
Be skeptical. Voice/style similarity is weak; sub-second action overlaps and shared narrow hashtag/reply patterns are strong.
Penalise mismatched hour-of-day distributions. Note when inputs are too sparse to judge.`

function compactInput(input: CorrelationInput): string {
  // Trim long arrays so the prompt stays small.
  const compact = {
    handles: input.handles.map((h) => ({
      handle: h.handle,
      totals: h.totals,
      hourBuckets: h.hourBuckets,
      medianGapMs: h.medianGapMs,
      meanGapMs: h.meanGapMs,
      subSecondBursts: h.subSecondBursts,
      zeroMsTimestamps: h.zeroMsTimestamps,
      samplePosts: h.samplePosts.slice(0, 25),
      topHashtags: h.topHashtags,
      topReplyTargets: h.topReplyTargets,
      earliest: h.earliest,
      latest: h.latest,
    })),
    pairwise: input.pairwise,
  }
  return JSON.stringify(compact)
}

const DEFAULT_MODEL: Record<AiProvider, string> = {
  openai: 'gpt-4o-mini',
  anthropic: 'claude-haiku-4-5-20251001',
  gemini: 'gemini-1.5-flash',
  xai: 'grok-2-latest',
}

async function callOpenAi(payload: string, apiKey: string, baseUrl: string, model: string): Promise<string> {
  const r = await fetch(`${baseUrl}/v1/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({
      model,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: payload },
      ],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as { choices?: Array<{ message?: { content?: string } }> }
  return data.choices?.[0]?.message?.content ?? ''
}

async function callAnthropic(payload: string, apiKey: string, model: string): Promise<string> {
  const r = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model,
      max_tokens: 4096,
      system: SYSTEM_PROMPT,
      messages: [{ role: 'user', content: payload }],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as { content?: Array<{ type?: string; text?: string }> }
  const block = (data.content ?? []).find((b) => b.type === 'text')
  return block?.text ?? ''
}

async function callGemini(payload: string, apiKey: string, model: string): Promise<string> {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      systemInstruction: { parts: [{ text: SYSTEM_PROMPT }] },
      contents: [{ role: 'user', parts: [{ text: payload }] }],
      generationConfig: { responseMimeType: 'application/json' },
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>
  }
  return data.candidates?.[0]?.content?.parts?.[0]?.text ?? ''
}

function tryParseJson<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T
  } catch {
    /* fall through */
  }
  const m = s.match(/\{[\s\S]*\}/)
  if (!m) return null
  try {
    return JSON.parse(m[0]) as T
  } catch {
    return null
  }
}

export async function runCorrelation(opts: {
  handles: string[]
  provider: AiProvider
  apiKey: string
  model?: string
}): Promise<{ input: CorrelationInput; verdict: CorrelationVerdict }> {
  if (opts.handles.length < 2) throw new Error('need at least two handles')
  if (!opts.apiKey) throw new Error('AI api key required')
  const input = await buildCorrelationInput(opts.handles)
  const payload = compactInput(input)
  const model = opts.model || DEFAULT_MODEL[opts.provider]
  let raw = ''
  switch (opts.provider) {
    case 'openai':
      raw = await callOpenAi(payload, opts.apiKey, 'https://api.openai.com', model)
      break
    case 'xai':
      raw = await callOpenAi(payload, opts.apiKey, 'https://api.x.ai', model)
      break
    case 'anthropic':
      raw = await callAnthropic(payload, opts.apiKey, model)
      break
    case 'gemini':
      raw = await callGemini(payload, opts.apiKey, model)
      break
  }
  const verdict = tryParseJson<CorrelationVerdict>(raw)
  if (!verdict) throw new Error('AI returned an unparseable response')
  return { input, verdict }
}
